import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { brandOptions } from '../../../constants/brands';
import { supabaseHelpers } from '../../../lib/supabase';
import { settingsStore } from '../../../lib/localSettingsStore';
import { useAuth } from '../../../contexts/AuthContext';

const AddFilamentDrawer = ({ isOpen, onClose, onSave, availableSuppliers }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const uploadTimersRef = useRef({});
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    brand: '',
    material: 'PLA',
    colorName: '',
    colorHex: '#ffffff',
    originalWeight: '',
    currentWeight: '',
    totalCost: '',
    purchaseDate: new Date()?.toISOString()?.split('T')?.[0],
    diameter: '1.75',
    density: 1.24,
    gramsPerMeter: '',
    supplier: '',
    image_urls: [],
    notes: ''
  });

  // Marcas comuns + globais (Settings)
  const commonBrandOptions = brandOptions;
  const [globalFilamentBrands, setGlobalFilamentBrands] = useState([]);
  useEffect(() => {
    let active = true;
    const loadBrands = async () => {
      try {
        const { data } = await settingsStore.getFilamentBrands();
        if (active) setGlobalFilamentBrands(Array.isArray(data) ? data : []);
      } catch {}
    };
    if (isOpen) loadBrands();
    return () => { active = false; };
  }, [isOpen]);

  const mergedBrandOptions = useMemo(() => {
    const set = new Set([
      ...commonBrandOptions.map(o => o?.label || o?.value),
      ...globalFilamentBrands
    ].filter(Boolean).map(s => String(s).trim()));
    const arr = Array.from(set).sort((a,b)=>a.localeCompare(b));
    const opts = arr.map(b => ({ value: b, label: b }));
    opts.push({ value: '__add_brand__', label: 'Adicionar marca...', badge: { text: 'Novo', variant: 'success' } });
    return opts;
  }, [commonBrandOptions, globalFilamentBrands]);

  const [brandMode, setBrandMode] = useState('select'); // 'select' | 'custom'
  const [supplierMode, setSupplierMode] = useState('select'); // 'select' | 'custom'
  const [isAddSupplierModalOpen, setIsAddSupplierModalOpen] = useState(false);
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newBrandName, setNewBrandName] = useState('');

  const [globalSuppliers, setGlobalSuppliers] = useState([]);
  useEffect(() => {
    let active = true;
    const loadSuppliers = async () => {
      try {
        const { data } = await settingsStore.getSuppliers();
        if (active) setGlobalSuppliers(Array.isArray(data) ? data : []);
      } catch {}
    };
    if (isOpen) loadSuppliers();
    return () => { active = false; };
  }, [isOpen]);

  const supplierOptions = useMemo(() => {
    const set = new Set(
      [
        ...((availableSuppliers || []).map(s => String(s || '').trim())),
        ...((globalSuppliers || []).map(s => String(s || '').trim()))
      ].filter(Boolean)
    );
    const arr = Array.from(set).sort((a, b) => a.localeCompare(b));
    const opts = arr.map(s => ({ value: s, label: s }));
    opts.push({ value: '__add_supplier__', label: 'Adicionar fornecedor...', badge: { text: 'Novo', variant: 'success' } });
    return opts;
  }, [availableSuppliers, globalSuppliers]);

  const [errors, setErrors] = useState({});

  const materialOptions = [
    { value: 'PLA', label: 'PLA (Ácido Polilático)', density: 1.24 },
    { value: 'ABS', label: 'ABS (Acrilonitrila Butadieno Estireno)', density: 1.04 },
    { value: 'PETG', label: 'PETG (Politereftalato de Etileno Glicol)', density: 1.27 },
    { value: 'TPU', label: 'TPU (Poliuretano Termoplástico)', density: 1.21 },
    { value: 'ASA', label: 'ASA (Acrilonitrila Estireno Acrilato)', density: 1.07 },
    { value: 'WOOD', label: 'Wood Fill (Preenchimento de Madeira)' },
    { value: 'CARBON_FIBER', label: 'Carbon Fiber (Fibra de Carbono)' },
    { value: 'METAL_FILLED', label: 'Metal Filled (Preenchimento Metálico)' },
    { value: 'PC', label: 'PC (Policarbonato)', density: 1.20 },
    { value: 'Nylon', label: 'Nylon', density: 1.13 },
    { value: 'HIPS', label: 'HIPS (Poliestireno de Alto Impacto)', density: 1.04 },
    { value: 'Other', label: 'Outro Material' }
  ];

  const diameterOptions = [
    { value: '1.75', label: '1.75mm (Padrão)' },
    { value: '2.85', label: '2.85mm' },
    { value: '3.00', label: '3.00mm' }
  ];

  const predefinedColors = [
    { name: 'Branco', hex: '#ffffff' },
    { name: 'Preto', hex: '#000000' },
    { name: 'Vermelho', hex: '#dc2626' },
    { name: 'Azul', hex: '#2563eb' },
    { name: 'Verde', hex: '#16a34a' },
    { name: 'Amarelo', hex: '#ca8a04' },
    { name: 'Laranja', hex: '#ea580c' },
    { name: 'Roxo', hex: '#9333ea' },
    { name: 'Rosa', hex: '#ec4899' },
    { name: 'Cinza', hex: '#6b7280' },
    { name: 'Transparente', hex: '#f3f4f6' },
    { name: 'Dourado', hex: '#eab308' }
  ];

  const computeGramatura = (density, diameterMm) => {
    const dcm = (parseFloat(diameterMm) || 0) / 10; // mm -> cm
    const r = dcm / 2;
    const volumePerMeterCm3 = Math.PI * r * r * 100; // área * 100cm (1m)
    return (parseFloat(density) || 0) * volumePerMeterCm3; // g/m
  };

  const handleInputChange = useCallback((field, value) => {
    const updatedData = { ...formData, [field]: value };

    // Clear errors when user types
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    // Auto-set current weight equal to original weight if not manually set
    if (field === 'originalWeight' && !formData?.currentWeight) {
      updatedData.currentWeight = value;
    }

    // Auto-calculate cost per gram
    if (field === 'totalCost' || field === 'originalWeight') {
      const cost = parseFloat(updatedData?.totalCost) || 0;
      const weight = parseFloat(updatedData?.originalWeight) || 0;
      if (cost > 0 && weight > 0) {
        updatedData.costPerGram = (cost / weight)?.toFixed(4);
      } else {
        delete updatedData.costPerGram;
      }
    }

    // Update density when material changes (if known)
    if (field === 'material') {
      const opt = materialOptions?.find(m => m?.value === value);
      if (opt?.density) {
        updatedData.density = opt?.density;
      }
    }

    // Recalculate gramatura (g/m) when material, diameter or density changes
    // Only auto-update if user hasn't set gramsPerMeter manually
    if (['material', 'diameter', 'density'].includes(field)) {
      const opt = materialOptions?.find(m => m?.value === updatedData?.material);
      const densityVal = parseFloat(updatedData?.density ?? opt?.density ?? 1.24);
      const diameterMm = parseFloat(updatedData?.diameter ?? '1.75');
      const gpm = computeGramatura(densityVal, diameterMm);
      if (Number.isFinite(gpm) && (formData?.gramsPerMeter === '' || formData?.gramsPerMeter == null)) {
        updatedData.gramsPerMeter = parseFloat(gpm.toFixed(3));
      }
    }

    // Normalize gramsPerMeter input to number with 3 decimals
    if (field === 'gramsPerMeter') {
      const raw = String(value || '')?.replace(',', '.').replace(/[^0-9.]/g, '');
      const parts = raw.split('.');
      const normalized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join('').slice(0,3)}` : parts[0];
      updatedData.gramsPerMeter = normalized;
    }

    setFormData(updatedData);
  }, [formData, errors, materialOptions]);

  const handleColorSelect = (color) => {
    setFormData(prev => ({
      ...prev,
      colorName: color?.name,
      colorHex: color?.hex
    }));
  };

  const handleFilesUpload = async (files) => {
    if (!user?.id) return;
    if (!files || files?.length === 0) return;

    setUploadingImage(true);
    try {
      // Limit to 5 images total
      const remainingSlots = Math.max(0, 5 - (formData?.image_urls?.length || 0));
      const filesToUpload = Array.from(files)?.slice(0, remainingSlots);

      const uploadedUrls = [];
      for (const file of filesToUpload) {
        // Initialize progress for this file and start a simulated progress timer
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        const timerId = setInterval(() => {
          setUploadProgress(prev => {
            const current = prev[file.name] ?? 0;
            const increment = Math.floor(Math.random() * 12) + 5; // +5..+16
            const next = Math.min(current + increment, 90); // cap at 90% until upload finishes
            return { ...prev, [file.name]: next };
          });
        }, 200);
        uploadTimersRef.current[file.name] = timerId;

        const { publicUrl } = await supabaseHelpers?.uploadFilamentImage(file, user?.id);
        if (publicUrl) uploadedUrls.push(publicUrl);

        // Mark as complete and clear timer
        if (uploadTimersRef.current[file.name]) {
          clearInterval(uploadTimersRef.current[file.name]);
          delete uploadTimersRef.current[file.name];
        }
        setUploadProgress(prev => ({ ...prev, [file.name]: 100 }));
        // Auto-clear this file's progress after a short delay
        setTimeout(() => {
          setUploadProgress(prev => {
            const { [file.name]: _, ...rest } = prev;
            return rest;
          });
        }, 1200);
      }

      setFormData(prev => ({ ...prev, image_urls: [...(prev?.image_urls || []), ...uploadedUrls] }));
    } catch (error) {
      console.error('Erro ao fazer upload das imagens:', error);
      alert('Erro ao fazer upload das imagens. Tente novamente.');
    } finally {
      setUploadingImage(false);
      // Ensure all timers are cleared
      Object.values(uploadTimersRef.current || {}).forEach(id => clearInterval(id));
      uploadTimersRef.current = {};
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.brand?.trim()) newErrors.brand = 'Marca é obrigatória';
    if (!formData?.colorName?.trim()) newErrors.colorName = 'Nome da cor é obrigatório';
    if (!formData?.originalWeight) newErrors.originalWeight = 'Peso original é obrigatório';
    if (!formData?.currentWeight) newErrors.currentWeight = 'Peso atual é obrigatório';
    if (!formData?.totalCost) newErrors.totalCost = 'Custo total é obrigatório';
    
    if (parseFloat(formData?.currentWeight) > parseFloat(formData?.originalWeight)) {
      newErrors.currentWeight = 'Peso atual não pode ser maior que o peso original';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      // Persistir fornecedor global, se novo (defensivo)
      const supplierName = String(formData?.supplier || '').trim();
      if (supplierName) {
        const { data: suppliers } = await settingsStore.getSuppliers();
        const exists = Array.isArray(suppliers)
          && suppliers.some((x) => String(x || '')?.trim()?.toLowerCase() === supplierName.toLowerCase());
        if (!exists) {
          await settingsStore.createSupplier(supplierName);
        }
      }

      // Persistir marca de filamento global, se novo (defensivo)
      const brandName = String(formData?.brand || '').trim();
      if (brandName) {
        const { data: brands } = await settingsStore.getFilamentBrands();
        const exists = Array.isArray(brands)
          && brands.some((x) => String(x || '')?.trim()?.toLowerCase() === brandName.toLowerCase());
        if (!exists) {
          await settingsStore.createFilamentBrand(brandName);
        }
      }

      const opt = materialOptions?.find(m => m?.value === formData?.material);
      const resolvedDensity = parseFloat(formData?.density ?? opt?.density ?? 1.24);
      const diameterMm = parseFloat(formData?.diameter || '1.75');
      const gramsPerMeter = Number.isFinite(parseFloat(formData?.gramsPerMeter))
        ? parseFloat(formData?.gramsPerMeter)
        : computeGramatura(resolvedDensity, diameterMm);

      const filamentData = {
        id: Date.now()?.toString(),
        ...formData,
        price_per_kg: parseFloat(formData?.totalCost) / (parseFloat(formData?.originalWeight) / 1000),
        stock_grams: parseInt(formData?.currentWeight),
        density: resolvedDensity,
        grams_per_meter: Number.isFinite(gramsPerMeter) ? parseFloat(gramsPerMeter.toFixed(3)) : undefined,
        costPerGram: parseFloat(formData?.totalCost) / parseFloat(formData?.originalWeight),
        createdAt: new Date()?.toISOString(),
        imageAlt: `Rolo de filamento ${formData?.material} ${formData?.colorName} da marca ${formData?.brand}`,
        image: (formData?.image_urls?.[0]) || 'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=400',
        image_urls: formData?.image_urls || []
      };

      await onSave(filamentData);
      
      // Reset form
      setFormData({
        brand: '',
        material: 'PLA',
        colorName: '',
        colorHex: '#ffffff',
        originalWeight: '',
        currentWeight: '',
        totalCost: '',
        purchaseDate: new Date()?.toISOString()?.split('T')?.[0],
        diameter: '1.75',
        density: 1.24,
        gramsPerMeter: '',
        supplier: '',
        image_urls: [],
        notes: ''
      });
      
      onClose();
    } catch (error) {
      console.error('Erro ao salvar filamento:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
        className="fixed right-0 top-0 h-full w-full max-w-lg bg-background border-l border-border z-50 overflow-y-auto shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Adicionar Filamento</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Registre um novo filamento no seu inventário
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
            />
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              <span>Informações do Material</span>
            </h3>
            
            {/* Marca: comum + globais + adicionar via modal */}
            {brandMode === 'select' ? (
              <div className="space-y-2">
                <Select
                  label="Marca"
                  options={mergedBrandOptions}
                  value={formData?.brand}
                  onChange={(value) => {
                    if (value === '__add_brand__') { setIsAddBrandModalOpen(true); return; }
                    handleInputChange('brand', value);
                  }}
                  searchable
                  clearable
                  required
                  description="Escolha uma marca ou adicione uma nova."
                />
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => setIsAddBrandModalOpen(true)}
                >
                  Não encontrou? Adicionar nova marca
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  label="Marca"
                  type="text"
                  placeholder="Digite o nome da marca"
                  value={formData?.brand}
                  onChange={(e) => handleInputChange('brand', e?.target?.value)}
                  error={errors?.brand}
                  required
                />
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:underline"
                  onClick={() => setBrandMode('select')}
                >
                  Preferir selecionar em uma lista
                </button>
              </div>
            )}

            <Select
              label="Tipo de Material"
              options={materialOptions}
              value={formData?.material}
              onChange={(value) => handleInputChange('material', value)}
              required
            />

            <Select
              label="Diâmetro do Filamento"
              options={diameterOptions}
              value={formData?.diameter}
              onChange={(value) => handleInputChange('diameter', value)}
            />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Densidade (g/cm³)"
              type="number"
              step="0.01"
              placeholder="1.24"
              value={formData?.density}
              onChange={(e) => handleInputChange('density', e?.target?.value)}
              description="Usada para calcular a gramatura (g/m)"
            />
            <Input
              label="Gramatura (g/m)"
              type="number"
              step="0.001"
              placeholder="Calculado automaticamente"
              value={formData?.gramsPerMeter}
              onChange={(e) => handleInputChange('gramsPerMeter', e?.target?.value)}
              description="Opcional: sobrescreve o cálculo automático"
            />
            {(() => {
                const densityVal = parseFloat(formData?.density || 0);
                const diameterMm = parseFloat(formData?.diameter || '1.75');
                const gpm = Number.isFinite(parseFloat(formData?.gramsPerMeter)) && parseFloat(formData?.gramsPerMeter) > 0
                  ? parseFloat(formData?.gramsPerMeter)
                  : computeGramatura(densityVal, diameterMm);
                if (!Number.isFinite(gpm) || gpm <= 0) return null;
                const metersOriginal = parseFloat(formData?.originalWeight || 0) > 0 ? (parseFloat(formData?.originalWeight) / gpm) : null;
                return (
                  <div className="bg-muted rounded-lg p-3 border border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Gramatura estimada</span>
                      <span className="text-sm font-semibold text-foreground">{gpm.toFixed(3)} g/m</span>
                    </div>
                    {metersOriginal && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Metragem (rolo novo): <span className="font-medium text-foreground">{metersOriginal.toFixed(1)} m</span>
                      </div>
                    )}
                  </div>
                );
              })()}
          </div>
          </div>

          {/* Color Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-secondary rounded-full"></span>
              <span>Cor do Filamento</span>
            </h3>

            {/* Color Palette */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Cores Populares
              </label>
              <div className="grid grid-cols-6 gap-2">
                {predefinedColors?.map((color) => (
                  <button
                    key={color?.hex}
                    type="button"
                    onClick={() => handleColorSelect(color)}
                    className={`
                      w-12 h-12 rounded-lg border-2 transition-all duration-200 hover:scale-105 relative
                      ${formData?.colorHex === color?.hex 
                        ? 'border-primary ring-2 ring-primary/20 scale-105' :'border-border hover:border-primary/50'}
                    `}
                    style={{ backgroundColor: color?.hex }}
                    title={color?.name}
                  >
                    {formData?.colorHex === color?.hex && (
                      <div className="absolute inset-0 rounded-lg bg-primary/20 flex items-center justify-center">
                        <div className="w-4 h-4 bg-primary rounded-full"></div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Input
                  label="Nome da Cor"
                  type="text"
                  placeholder="Ex: Azul Royal, Preto Fosco..."
                  value={formData?.colorName}
                  onChange={(e) => handleInputChange('colorName', e?.target?.value)}
                  error={errors?.colorName}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Código Hex
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={formData?.colorHex}
                    onChange={(e) => handleInputChange('colorHex', e?.target?.value)}
                    className="w-full h-10 rounded-lg border border-border cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Weight & Cost */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-accent rounded-full"></span>
              <span>Peso e Custo</span>
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Peso Original (g)"
                type="number"
                placeholder="1000"
                value={formData?.originalWeight}
                onChange={(e) => handleInputChange('originalWeight', e?.target?.value)}
                error={errors?.originalWeight}
                description="Peso do rolo novo"
                required
              />

              <Input
                label="Peso Atual (g)"
                type="number"
                placeholder="1000"
                value={formData?.currentWeight}
                onChange={(e) => handleInputChange('currentWeight', e?.target?.value)}
                error={errors?.currentWeight}
                description="Peso restante"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Custo Total (R$)"
                type="number"
                step="0.01"
                placeholder="89.90"
                value={formData?.totalCost}
                onChange={(e) => handleInputChange('totalCost', e?.target?.value)}
                error={errors?.totalCost}
                required
              />

              <Input
                label="Data da Compra"
                type="date"
                value={formData?.purchaseDate}
                onChange={(e) => handleInputChange('purchaseDate', e?.target?.value)}
              />
            </div>

            {/* Cost per gram display */}
            {formData?.totalCost && formData?.originalWeight && (
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-4 border border-primary/20">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-muted-foreground">Custo por grama:</span>
                  <span className="text-xl font-bold text-foreground">
                    R$ {((parseFloat(formData?.totalCost) || 0) / (parseFloat(formData?.originalWeight) || 1))?.toFixed(4)}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-muted-foreground">Custo por quilograma:</span>
                  <span className="text-lg font-semibold text-primary">
                    R$ {(((parseFloat(formData?.totalCost) || 0) / (parseFloat(formData?.originalWeight) || 1)) * 1000)?.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {/* Approximate length display */}
            {(() => {
              const densityVal = parseFloat(formData?.density ?? 1.24);
              const diameterMm = parseFloat(formData?.diameter ?? '1.75');
              const gpm = Number.isFinite(parseFloat(formData?.gramsPerMeter)) && parseFloat(formData?.gramsPerMeter) > 0
                ? parseFloat(formData?.gramsPerMeter)
                : computeGramatura(densityVal, diameterMm);
              if (!Number.isFinite(gpm) || gpm <= 0) return null;
              const origG = parseFloat(formData?.originalWeight || 0);
              const currG = parseFloat(formData?.currentWeight || 0);
              const metersOriginal = origG > 0 ? (origG / gpm) : null;
              const metersCurrent = currG > 0 ? (currG / gpm) : null;
              return (
                <div className="bg-muted rounded-lg p-4 border border-border">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Comprimento aproximado</div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Rolo</span>
                    <span className="text-sm text-foreground">{origG || 0}g{metersOriginal ? ` · ${metersOriginal.toFixed(1)} m` : ''}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-sm text-muted-foreground">Restante</span>
                    <span className="text-sm text-foreground">{currG || 0}g{metersCurrent ? ` · ${metersCurrent.toFixed(1)} m` : ''}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Image Upload */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-success rounded-full"></span>
              <span>Imagens do Filamento</span>
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Selecionar imagens (até 5)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = e?.target?.files;
                    if (files) handleFilesUpload(files);
                  }}
                  className="sr-only"
                  disabled={uploadingImage}
                />
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    iconName="Upload"
                    iconPosition="left"
                    onClick={() => fileInputRef?.current?.click()}
                    disabled={uploadingImage}
                  >
                    Selecionar imagens
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {Array.isArray(formData?.image_urls) && formData?.image_urls?.length > 0
                      ? `${formData?.image_urls?.length} imagem(ns) selecionada(s)`
                      : 'Nenhuma imagem selecionada'}
                  </span>
                </div>
                {uploadingImage && (
                  <p className="text-sm text-primary mt-2 flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <span>Enviando imagens...</span>
                  </p>
                )}
                {Object.keys(uploadProgress)?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {Object.entries(uploadProgress).map(([name, pct]) => (
                      <div key={name} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground truncate max-w-[60%]" title={name}>{name}</span>
                          <span className="text-xs text-foreground">{Math.round(pct)}%</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                          <div className={`h-2 rounded-full ${pct < 100 ? 'bg-primary' : 'bg-success'}`} style={{ width: `${Math.max(3, Math.min(100, pct))}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Gallery Preview */}
              {formData?.image_urls?.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {formData?.image_urls?.map((url, idx) => (
                    <div key={idx} className="relative w-full h-24 rounded-lg overflow-hidden border border-border bg-muted">
                      <img src={url} alt={`Imagem ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        className="absolute top-2 right-2 px-2 py-1 text-xs bg-black/60 text-white rounded"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            image_urls: prev?.image_urls?.filter((_, i) => i !== idx)
                          }));
                        }}
                      >Remover</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Supplier */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-secondary rounded-full"></span>
              <span>Fornecedor</span>
            </h3>
            {supplierMode === 'select' ? (
              <div className="space-y-2">
                <Select
                  label="Fornecedor"
                  options={supplierOptions}
                  value={formData?.supplier}
                  onChange={(value) => {
                    if (value === '__add_supplier__') { setIsAddSupplierModalOpen(true); return; }
                    handleInputChange('supplier', value);
                  }}
                  searchable
                  clearable
                  description="Escolha um fornecedor existente ou adicione um novo."
                />
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() => setIsAddSupplierModalOpen(true)}
                >
                  Não encontrou? Adicionar fornecedor
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  label="Fornecedor"
                  type="text"
                  placeholder="Digite o nome do fornecedor"
                  value={formData?.supplier}
                  onChange={(e) => handleInputChange('supplier', e?.target?.value)}
                />
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:underline"
                  onClick={() => setSupplierMode('select')}
                >
                  Preferir selecionar em uma lista
                </button>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <span className="w-2 h-2 bg-warning rounded-full"></span>
              <span>Observações</span>
            </h3>

            <Input
              label="Notas"
              type="textarea"
              placeholder="Configurações de impressão recomendadas, características especiais do material..."
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              rows={3}
            />
          </div>

          {/* Action Buttons */}
          <div className="sticky bottom-0 bg-background border-t border-border p-6 -mx-6 -mb-6">
            <div className="flex items-center space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={isLoading}
                iconName="Plus"
                iconPosition="left"
                className="flex-1"
              >
                Adicionar Filamento
              </Button>
            </div>
          </div>
        </form>
      </motion.div>
      {/* Modal para adicionar fornecedor */}
      {isAddSupplierModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsAddSupplierModalOpen(false)} />
          <div className="relative bg-background border border-border rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Adicionar novo fornecedor</h3>
            <Input
              label="Nome do fornecedor"
              type="text"
              placeholder="Ex: Amazon, FilipeFlop..."
              value={newSupplierName}
              onChange={(e) => setNewSupplierName(e?.target?.value)}
            />
            <div className="mt-6 flex items-center gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setIsAddSupplierModalOpen(false)}>Cancelar</Button>
              <Button className="flex-1" onClick={async () => {
                const name = String(newSupplierName || '').trim();
                if (!name) return;
                const { data: created } = await settingsStore.createSupplier(name);
                setNewSupplierName('');
                setIsAddSupplierModalOpen(false);
                // Refresh list
                const { data } = await settingsStore.getSuppliers();
                setGlobalSuppliers(Array.isArray(data) ? data : []);
                setFormData(prev => ({ ...prev, supplier: created || name }));
              }}>Adicionar</Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Modal para adicionar marca */}
      {isAddBrandModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsAddBrandModalOpen(false)} />
          <div className="relative bg-background border border-border rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Adicionar nova marca de filamento</h3>
            <Input
              label="Nome da marca"
              type="text"
              placeholder="Ex: Prusament, eSUN..."
              value={newBrandName}
              onChange={(e) => setNewBrandName(e?.target?.value)}
            />
            <div className="mt-6 flex items-center gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setIsAddBrandModalOpen(false)}>Cancelar</Button>
              <Button className="flex-1" onClick={async () => {
                const name = String(newBrandName || '').trim();
                if (!name) return;
                const { data: created } = await settingsStore.createFilamentBrand(name);
                setNewBrandName('');
                setIsAddBrandModalOpen(false);
                // Refresh list
                const { data } = await settingsStore.getFilamentBrands();
                setGlobalFilamentBrands(Array.isArray(data) ? data : []);
                setFormData(prev => ({ ...prev, brand: created || name }));
              }}>Adicionar</Button>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
};

export default AddFilamentDrawer;